import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';

import { supabase } from '../supabase';
import { fetchEvents } from '../Utils/EventUtils';
import { fetchVenueData } from '../Utils/VenueUtils';
import SportVlaanderenHeader from '../Components/common/Headers';
import EventListView from '../Components/events/EventListView';
import EventCalendarView from '../Components/events/EventCalendarView';
import EventGridView from '../Components/events/EventGridView';

const VIEWS = ['list', 'calendar', 'grid'];

const Events = () => {
  const { venueId: routeVenueId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const venueId = parseInt(routeVenueId || searchParams.get('venue_id') || '1', 10);
  const requestedView = searchParams.get('view');
  const view = VIEWS.includes(requestedView) ? requestedView : 'list';

  const [venueData, setVenueData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadEvents = useCallback(async () => {
    const data = await fetchEvents(venueId);
    setEvents(data);
  }, [venueId]);

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    setError(null);

    Promise.all([fetchVenueData(venueId), fetchEvents(venueId)])
      .then(([venue, evts]) => {
        if (!isActive) return;
        setVenueData(venue);
        setEvents(evts);
      })
      .catch((err) => {
        console.error('Error loading events overview:', err);
        if (isActive) setError('Er is een fout opgetreden bij het laden van de events.');
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [venueId]);

  useEffect(() => {
    if (!venueId) return undefined;

    const channel = supabase
      .channel(`schedule-events-${venueId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'schedule' },
        (payload) => {
          const record = payload.new?.venue_id ? payload.new : payload.old;
          if (record && parseInt(record.venue_id) === venueId) {
            loadEvents();
          }
        }
      )
      .subscribe();

    return () => channel.unsubscribe();
  }, [venueId, loadEvents]);

  const handleViewChange = (_event, newView) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', newView);
    setSearchParams(params);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SportVlaanderenHeader venueData={venueData} />

      <Container maxWidth="xl" sx={{ flexGrow: 1, py: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Programma-overzicht
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Tabs value={view} onChange={handleViewChange} textColor="primary" indicatorColor="primary">
            <Tab value="list" label="Lijst" icon={<ViewListIcon />} iconPosition="start" />
            <Tab value="calendar" label="Kalender" icon={<CalendarMonthIcon />} iconPosition="start" />
            <Tab value="grid" label="Rooster" icon={<ViewWeekIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {view === 'list' && <EventListView events={events} />}
        {view === 'calendar' && <EventCalendarView events={events} />}
        {view === 'grid' && <EventGridView events={events} />}
      </Container>
    </Box>
  );
};

export default Events;
